import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import firebase from 'firebase/app';
import 'firebase/firestore';

import { AuthService } from '../../services/auth.service';
import { CustomActions } from '../../dictionaries/CustomActions';

interface ImagingCase {
  id: string;
  label: string;
  status: string;
  firmId: string;
  uploadSessionIds: string[];
  clioMatterId: string | null;
  clioMatterName: string | null;
  createdAt: any;
  updatedAt: any;
}

@Component({
  selector: 'app-imaging-cases',
  templateUrl: './imaging-cases.component.html',
  styleUrls: ['./imaging-cases.component.scss'],
})
export class ImagingCasesComponent implements OnInit, OnDestroy {
  cases: ImagingCase[] = [];
  filteredCases: ImagingCase[] = [];
  loading = true;
  searchQuery = '';

  displayedColumns = ['label', 'status', 'uploads', 'clio', 'createdAt', 'actions'];

  private firmId: string;
  private casesLoaded = false;
  private userDataSub: Subscription;

  constructor(
    private auth_$: AuthService,
    private router: Router,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.auth_$.preventRedirections = true;
    this.userDataSub = this.auth_$.userData.subscribe((data: any) => {
      if (data?.uid && !this.casesLoaded) {
        this.casesLoaded = true;
        this.firmId = data.ownerID || data.uid;
        this.loadCases();
      }
    });
  }

  ngOnDestroy() {
    this.userDataSub?.unsubscribe();
  }

  onSearchChange() {
    const q = this.searchQuery.trim().toLowerCase();
    this.filteredCases = q
      ? this.cases.filter(c => c.label.toLowerCase().includes(q))
      : [...this.cases];
  }

  openDetail(id: string) {
    this.router.navigate([CustomActions.imagingCases, id]);
  }

  goToUpload() {
    this.router.navigate([CustomActions.addMedicalDisk]);
  }

  statusColor(status: string): string {
    const map: Record<string, string> = {
      pending:    'status-pending',
      queued:     'status-queued',
      processing: 'status-processing',
      processed:  'status-processed',
      linked:     'status-linked',
      failed:     'status-failed',
    };
    return map[status] || 'status-pending';
  }

  statusIcon(status: string): string {
    const map: Record<string, string> = {
      pending:    'schedule',
      queued:     'queue',
      processing: 'sync',
      processed:  'check_circle',
      linked:     'link',
      failed:     'error',
    };
    return map[status] || 'schedule';
  }

  private async loadCases() {
    if (!this.firmId) return;
    try {
      const snap = await firebase.firestore()
        .collection('imagingCases')
        .where('firmId', '==', this.firmId)
        .orderBy('createdAt', 'desc')
        .get();
      this.ngZone.run(() => {
        this.cases = snap.docs.map(d => ({
          id: d.id,
          ...(d.data() as Omit<ImagingCase, 'id'>),
        }));
        this.filteredCases = [...this.cases];
        this.loading = false;
      });
    } catch (err) {
      console.error('loadCases error:', err);
      this.ngZone.run(() => { this.loading = false; });
    }
  }
}
